/**
 * patientHistoryStore.ts
 *
 * Tracks recently visited patients and pinned patients.
 * Persisted server-side (per user) via /v5/recent-patients, so the list
 * survives logout/relogin and follows the user across devices.
 *
 * The store remains the single source of truth for UI; mutations are
 * applied optimistically and the server call happens in the background.
 * A hydrate() call (fire-and-forget) is made once on authenticated mount.
 */
import { create } from 'zustand';
import { recentPatientsService, type RecentPatientDTO } from '@/services/recentPatients.service';
import { useAuthStore } from '@/store/authStore';
import { sidebarPrefs } from '@/lib/sidebarPrefs';

export interface PatientHistoryEntry {
  patientId: number;
  name: string;
  photoUrl?: string | null;
  pinned: boolean;
  visitedAt: number;
  pinnedAt: number | null;
}

interface PatientHistoryState {
  entries: PatientHistoryEntry[];
  sidebarOpen: boolean;
  hydrated: boolean;

  /** Fetch server-side list and replace local state. No-op if already hydrated. */
  hydrate: (opts?: { force?: boolean }) => Promise<void>;
  /**
   * Record a patient visit — adds to top or bumps existing entry.
   *
   * `photoUrl` follows the usePatientPhoto tri-state: `null` means the caller
   * KNOWS there is no photo (overwrites), a string is the URL, and `undefined`
   * means the caller does not know — an existing entry keeps whatever it had.
   */
  visit: (patient: { patientId: number; name: string; photoUrl?: string | null }) => void;
  /** Pin/unpin a patient to keep them in the list */
  togglePin: (patientId: number) => void;
  /** Remove a patient from the list */
  remove: (patientId: number) => void;
  /** Clear all non-pinned entries */
  clearHistory: () => void;
  /** Clear all in-memory entries (used on logout so the next user starts fresh) */
  reset: () => void;
  /** Toggle sidebar open/collapsed */
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
}

/** Max non-pinned recent entries retained locally. Pinned entries are unlimited. */
const MAX_RECENT = 50;

function parseDateMs(value: string | null | undefined): number {
  if (!value) return 0;
  const asNumber = Date.parse(value);
  return Number.isFinite(asNumber) ? asNumber : 0;
}

function fromDTO(dto: RecentPatientDTO): PatientHistoryEntry {
  return {
    patientId: dto.patientID,
    name: dto.name,
    photoUrl: dto.photoUrl ?? null,
    pinned: Boolean(dto.pinned),
    visitedAt: parseDateMs(dto.visitedAt) || Date.now(),
    pinnedAt: dto.pinned ? parseDateMs(dto.pinnedAt) || Date.now() : null,
  };
}

function sortEntries(entries: PatientHistoryEntry[]): PatientHistoryEntry[] {
  const pinned = entries
    .filter((e) => e.pinned)
    .sort((a, b) => (b.pinnedAt ?? 0) - (a.pinnedAt ?? 0));
  const recent = entries
    .filter((e) => !e.pinned)
    .sort((a, b) => b.visitedAt - a.visitedAt)
    .slice(0, MAX_RECENT);
  return [...pinned, ...recent];
}

export const usePatientHistoryStore = create<PatientHistoryState>((set, get) => ({
  entries: [],
  // Read collapsed/expanded state from localStorage so it survives reload
  // and logout/login on the same browser. `reset()` (called on logout)
  // intentionally leaves this key alone.
  sidebarOpen: sidebarPrefs.readRecents(false),
  hydrated: false,

  hydrate: async ({ force = false } = {}) => {
    if (get().hydrated && !force) return;
    try {
      const dtos = await recentPatientsService.list();
      set({
        entries: sortEntries(dtos.map(fromDTO)),
        hydrated: true,
      });
    } catch {
      // Swallow — leave local state untouched; next mutation will re-hydrate on failure.
      set({ hydrated: true });
    }
  },

  visit: (patient) => {
    const { entries } = get();
    const existing = entries.find((e) => e.patientId === patient.patientId);
    const now = Date.now();

    let updated: PatientHistoryEntry[];
    if (existing) {
      updated = entries.map((e) =>
        e.patientId === patient.patientId
          ? {
              ...e,
              name: patient.name || e.name,
              photoUrl: patient.photoUrl === undefined ? e.photoUrl : patient.photoUrl,
              visitedAt: now,
            }
          : e,
      );
    } else {
      updated = [
        {
          patientId: patient.patientId,
          name: patient.name,
          photoUrl: patient.photoUrl,
          pinned: false,
          visitedAt: now,
          pinnedAt: null,
        },
        ...entries,
      ];
    }

    set({ entries: sortEntries(updated) });

    recentPatientsService.visit(patient.patientId).catch(() => {
      void get().hydrate({ force: true });
    });
  },

  togglePin: (patientId) => {
    const { entries } = get();
    const target = entries.find((e) => e.patientId === patientId);
    if (!target) return;
    const nextPinned = !target.pinned;
    const now = Date.now();

    const updated = entries.map((e) =>
      e.patientId === patientId
        ? { ...e, pinned: nextPinned, pinnedAt: nextPinned ? now : null }
        : e,
    );
    set({ entries: sortEntries(updated) });

    recentPatientsService.setPin(patientId, nextPinned).catch(() => {
      void get().hydrate({ force: true });
    });
  },

  remove: (patientId) => {
    const { entries } = get();
    const updated = entries.filter((e) => e.patientId !== patientId);
    set({ entries: updated });

    recentPatientsService.remove(patientId).catch(() => {
      void get().hydrate({ force: true });
    });
  },

  clearHistory: () => {
    const { entries } = get();
    const pinned = entries.filter((e) => e.pinned);
    set({ entries: pinned });

    recentPatientsService.clearNonPinned().catch(() => {
      void get().hydrate({ force: true });
    });
  },

  reset: () => set({ entries: [], hydrated: false }),

  toggleSidebar: () =>
    set((s) => {
      const next = !s.sidebarOpen;
      sidebarPrefs.writeRecents(next);
      return { sidebarOpen: next };
    }),
  setSidebarOpen: (open) => {
    sidebarPrefs.writeRecents(open);
    set({ sidebarOpen: open });
  },
}));

// Clear the in-memory list when the user logs out so the next user on the
// same browser does not briefly see the previous user's recent patients.
// The next login will re-hydrate from the server.
useAuthStore.subscribe((state, prevState) => {
  if (prevState.token && !state.token) {
    usePatientHistoryStore.getState().reset();
  }
});
